/* eslint-disable no-underscore-dangle -- the __dhlConfig variable is named this way */
import { type AvailableFeatures, isFeatureKeyEnabled, buildConfig, topLevelDomainUtils } from '@dhl/receiver-config'
import {
  type JsonConfiguration,
  pwaTlds,
  type PwaTlds,
  type PwaAppConfiguration,
} from '../configuration/pwa/appconfiguration'
import localJsonConfig from '../configuration/pwa/local.json'

type InjectedConfig = {
  CSP_NONCE: string
  ENVIRONMENT: string
  VERSION: string
}

declare global {
  interface Window {
    __dhlConfig: JsonConfiguration<PwaTlds> & InjectedConfig
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    __REDUX_DEVTOOLS_EXTENSION__: any
  }
}

export const { getTopLevelDomain, buildConfigForTld } = topLevelDomainUtils.buildDomainUtils<PwaTlds>({
  supportedTlds: pwaTlds,
  defaultTld: 'nl',
  tldOverride: process.env.REACT_APP_UTIL_TOP_LEVEL_DOMAIN_OVERRIDE || window.__dhlConfig?.default.util.tldOverride,
  frontendDomain: 'my.dhlparcel',
})

/**
 * Build configuration
 */
const buildConfigInstance = () => {
  const tld = getTopLevelDomain()
  const isDevelopment = process.env.NODE_ENV !== 'production'

  // In development mode we use the local config, otherwise get the runtime config (i.e. window.__dhlConfig)
  const baseConfig = buildConfigForTld<PwaAppConfiguration, JsonConfiguration<PwaTlds>>({
    config: isDevelopment ? localJsonConfig : window.__dhlConfig,
    tld,
  })

  // We need to add some config settings that are not yet definable in the JSON config
  const additionalConfig = (config: PwaAppConfiguration) => ({
    basePath: process.env.REACT_APP_BASE_PATH ?? config.basePath,
    cspNonce: window.__dhlConfig?.CSP_NONCE ?? '',
    environment: window.__dhlConfig?.ENVIRONMENT ?? 'development',
    isDevelopment,
    version: window.__dhlConfig?.VERSION ?? '',
    prismic: {
      endpoint: `${window.location.origin}${baseConfig.prismic.endpoint}`,
    },
  })

  return buildConfig<PwaAppConfiguration, ReturnType<typeof additionalConfig>>({
    baseConfig,
    additionalConfig,
  })
}

/**
 * Configuration singleton
 */
let configInstance: ReturnType<typeof buildConfigInstance> | null = null

export const getConfig = (): ReturnType<typeof buildConfigInstance> => {
  configInstance ??= buildConfigInstance()
  return configInstance
}

/**
 * Feature enabled utils
 */
type FeatureKeys = AvailableFeatures<PwaAppConfiguration['features']>
export const isFeatureEnabled = (...featureKeys: FeatureKeys[]): boolean => {
  const config = getConfig()
  return featureKeys.some((featureKey) => isFeatureKeyEnabled(featureKey, config))
}
